/**
 * Saving a package download.
 *
 * The download endpoints are asked for `link=1`. For a large package they answer
 * `{url, expires_at}`: a read-only link to the file in Azure Files, valid for about ten minutes.
 * The browser downloads it itself through an `<a download>` click, so the transfer streams to disk
 * and survives a deploy of the API. Its download manager can resume an interrupted transfer only
 * while the link is valid; after that, starting the download again asks the API for a new link.
 * Every call asks for a new link, so a link is never reused. A smaller file comes back as the file
 * itself and is saved from memory, as before.
 */

export interface DownloadLink {
  url: string
  expires_at: string
}

function clickLink(href: string, fileName: string): void {
  const link = document.createElement('a')
  link.href = href
  link.download = fileName
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  link.remove()
}

function blobText(data: Blob): Promise<string> {
  if (typeof data.text === 'function') return data.text()
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(reader.error)
    reader.readAsText(data)
  })
}

/** The link in a response body, or null when the body is the file itself. */
export async function readDownloadLink(data: Blob, contentType?: string): Promise<DownloadLink | null> {
  if (!(contentType || data.type || '').includes('application/json')) return null
  const body = JSON.parse(await blobText(data))
  if (typeof body?.url !== 'string' || !isStorageLink(body.url)) {
    throw new Error('The download answer carries no Azure Files link')
  }
  return body as DownloadLink
}

/** Only an HTTPS link to Azure Files is followed. */
export function isStorageLink(url: string): boolean {
  try {
    const parsed = new URL(url)
    return parsed.protocol === 'https:' && parsed.hostname.endsWith('.file.core.windows.net')
  } catch {
    return false
  }
}

/** Starts the download from a `link=1` response: from the link, or from the file in the body. */
export async function saveDownload(data: Blob, contentType: string | undefined, fileName: string): Promise<void> {
  const link = await readDownloadLink(data, contentType)
  if (link) {
    clickLink(link.url, fileName)
    return
  }
  const url = URL.createObjectURL(data)
  try {
    clickLink(url, fileName)
  } finally {
    URL.revokeObjectURL(url)
  }
}
